import React, { useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Card from 'reactstrap/lib/Card';

import {
  PricingCreativeConfigDefaultProps,
  PricingCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import {
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { PRICING_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ConquestRibbon } from 'site-modules/shared/components/native-ad/native-ad-components/conquest-ribbon/conquest-ribbon';
import { addZipCode, definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { AdLabel } from 'site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';
import { Disclaimer } from 'site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';
import { getDisplayHeadline } from 'site-modules/shared/components/native-ad/utils/offer-incentives-ad-utils';
import { CPO_PRODUCT_SKU } from 'site-modules/shared/components/native-ad/native-ad-components/cpo-features/cpo-constants';

import { getNonMSRPDefaultLabel, isNonMSRPMake, isOemUrlWhitelisted } from './data-helper';
import './pricing-site-served-ad.scss';
import './pricing-horizontal-site-served-ad.scss';

const DEFAULT_HEADLINE = 'See Offers %%PATTERN:zip%%';

export function PricingHorizontalSiteServedAdUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  creativeConfig = PricingCreativeConfigDefaultProps,
  isConquest = SiteServedAdDefaultProps.isConquest,
  vehicle = SiteServedAdDefaultProps.vehicle,
  mobile = SiteServedAdDefaultProps.mobile,
  position = SiteServedAdDefaultProps.position,
  cardClassName = null,
  wrapperClass = null,
  ivtClassName = SiteServedAdDefaultProps.ivtClassName,
  showBorder = false,
  btnColorClass = null,
  linkDisplayUrlColor = 'text-blue-50',
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    ipZipCode,
    offersNumber,
    creativeConfigData: {
      photoDisclaimer,
      photo,
      logo,
      headline,
      msrp: msrpConfig,
      disclaimerCopy,
      lineItemId,
      siteServedCreativeId,
      linkDisplayUrl,
      cta,
      target,
      productSku,
    },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      adLoadTrackingListener(event, cta);
    },
    [adLoadTrackingListener, cta]
  );

  const msrp = msrpConfig?.trim();
  const make = get(vehicle, 'make.niceName', '');

  const isNonMsrpOem = isNonMSRPMake(make);
  const isWhiteListedUrl = isOemUrlWhitelisted(linkDisplayUrl);

  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';

  const noMSRPLabelDisplay = msrp || getNonMSRPDefaultLabel(make);
  const noMSRPLabel = !!noMSRPLabelDisplay && (
    <ContentFragment classes="label-container mx-0_5 display-3">{noMSRPLabelDisplay}</ContentFragment>
  );

  const nativeStyle = PRICING_AD.NATIVE_STYLE;

  const isCpoFeaturesEnabled = productSku?.includes(CPO_PRODUCT_SKU);
  const headlineToUse = isCpoFeaturesEnabled
    ? getDisplayHeadline(null, offersNumber, zipCode, ipZipCode, DEFAULT_HEADLINE, 15, 15)
    : msrp;

  const renderHeading = headlineToUse && (
    <div className="label-container text-center">
      <ContentFragment
        classes={classnames('text-cool-gray-10', {
          'heading-3': !isCpoFeaturesEnabled || !mobile,
          'heading-5': isCpoFeaturesEnabled && !mobile,
        })}
        componentToUse="span"
      >
        {headlineToUse}
      </ContentFragment>
      {!isCpoFeaturesEnabled && <div className="small text-gray-darker d-block text-cool-gray-40">Starting MSRP</div>}
    </div>
  );

  return (
    <div
      className={classnames(
        'pricing-site-served-ad-unit pricing-horizontal rounded w-100',
        wrapperClass,
        ivtClassName,
        {
          'is-whitelisted-oem-url': isWhiteListedUrl,
          'show-border': showBorder,
        }
      )}
      style={{ overflow: 'hidden' }}
    >
      <Card
        className={cardClassName}
        data-tracking-parent={nativeStyle}
        data-viewability={`${nativeStyle}-${position}`}
        innerRef={adRef}
      >
        <DfpTracking
          lineItemId={lineItemId}
          siteServedCreativeId={siteServedCreativeId}
          mobile={mobile}
          nativeStyle={nativeStyle}
          position={position}
          slotRenderEndListener={loadTrackingListener}
          siteServedAdsUpdater={siteServedAdsUpdater}
        />
        {adTrackers}
        <a
          href={ctaURL}
          target={ctaTarget}
          rel="noopener noreferrer"
          onClick={adClickTrackingListener}
          className="site-served-ad-link no-focus h-100 text-decoration-none overflow-hidden pb-0_5"
        >
          <AdLabel />
          {isConquest ? (
            <ConquestRibbon style={{ position: 'absolute', left: 0, top: 8, height: '20px' }} />
          ) : (
            <img
              className="logo"
              style={{ position: 'absolute', maxHeight: '24px' }}
              alt=""
              src={logo}
              loading="lazy"
            />
          )}
          <div className={classnames('img-wrapper ps-0_75', { 'mt-1': mobile })}>
            <img
              className={classnames('core-img img-fluid d-block mx-auto w-100', {
                'has-img-disclaimer': !!photoDisclaimer,
                background: !photo,
              })}
              style={{ maxHeight: '140px', objectFit: 'contain' }}
              src={photo}
              alt=""
              loading="lazy"
            />
          </div>
          <div
            className={classnames('ymm-info d-flex flex-column justify-content-center pe-0_75', {
              'my-auto': !mobile,
            })}
          >
            <div className="d-flex flex-column justify-content-center align-items-center">
              {(!isCpoFeaturesEnabled && isNonMsrpOem && noMSRPLabel) || renderHeading}
              {headline && (
                <Fragment>
                  <ContentFragment
                    classes={classnames('mb-0_5 mb-md-0 text-cool-gray-40', {
                      'heading-6': !isCpoFeaturesEnabled || !mobile,
                      'heading-7 fw-medium': isCpoFeaturesEnabled && mobile,
                    })}
                  >
                    {headline}
                  </ContentFragment>
                </Fragment>
              )}
            </div>
          </div>
          <div className="cta-container d-flex mx-0_75 mb-auto bg-blue-90 pos-r">
            <div
              className={classnames(
                'me-0_75 px-1 cta btn btn-blue-50 btn-sm text-transform-none size-16 py-0_25 pos-r',
                {
                  [btnColorClass]: !!btnColorClass,
                }
              )}
              style={{
                flex: '2 1 auto',
                borderTopRightRadius: '1rem',
                borderBottomRightRadius: '1rem',
                borderTopLeftRadius: '0.25rem',
                borderBottomLeftRadius: '0.25rem',
              }}
            >
              <CtaButtonText text={cta} />
            </div>
            <div
              className={classnames('fw-medium d-flex align-items-center size-12 pos-r', {
                [linkDisplayUrlColor]: !!linkDisplayUrlColor,
              })}
              style={{
                flex: '1 0 auto',
              }}
            >
              {linkDisplayUrl}
            </div>
          </div>
          {photoDisclaimer && <div className="ps-0_75 mt-0_5 text-gray-darker size-10">{photoDisclaimer}</div>}
        </a>
        {disclaimerCopy && <Disclaimer disclaimerText={disclaimerCopy} buttonStyle={{ bottom: 0 }} />}
      </Card>
    </div>
  );
}

PricingHorizontalSiteServedAdUI.propTypes = {
  ...SiteServedAdPropTypes,
  creativeConfig: PricingCreativeConfigPropTypes,
  cardClassName: PropTypes.string,
  wrapperClass: PropTypes.string,
  showBorder: PropTypes.bool,
  btnColorClass: PropTypes.string,
  linkDisplayUrlColor: PropTypes.string,
};

export const PricingHorizontalSiteServedAd = siteServedAdTrackingWrapper(PricingHorizontalSiteServedAdUI);
